import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();
  #animating = false;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // Take over the toggle to animate smoothly
    event.preventDefault();

    if (this.#animating) return;

    const isOpening = !this.details.open;

    if (isOpening) {
      // Close sibling accordions smoothly
      const parent = this.closest('.accordion');
      if (parent) {
        parent.querySelectorAll('accordion-custom').forEach((sibling) => {
          if (sibling !== this && sibling.querySelector('details')?.open) {
            sibling.#smoothClose();
          }
        });
      }
      this.#smoothOpen();
    } else {
      this.#smoothClose();
    }
  };

  #smoothOpen() {
    const details = this.details;
    const content = details.querySelector('.details-content');
    if (!content) { details.open = true; return; }

    this.#animating = true;

    // Set open so content renders (needed to measure height)
    details.open = true;

    // Measure the target height
    const targetHeight = content.scrollHeight;

    // Animate from 0 to full height
    const animation = content.animate(
      [
        { height: '0px', opacity: 0, overflow: 'clip', paddingBlock: '0px' },
        { height: targetHeight + 'px', opacity: 1, overflow: 'clip' }
      ],
      { duration: 350, easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)' }
    );

    animation.finished.then(() => {
      this.#animating = false;
    }).catch(() => {
      this.#animating = false;
    });
  }

  #smoothClose() {
    const details = this.details;
    const content = details.querySelector('.details-content');
    if (!content) { details.open = false; return; }

    this.#animating = true;

    const startHeight = content.scrollHeight;

    // Animate from full height to 0
    const animation = content.animate(
      [
        { height: startHeight + 'px', opacity: 1, overflow: 'clip' },
        { height: '0px', opacity: 0, overflow: 'clip', paddingBlock: '0px' }
      ],
      { duration: 350, easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)' }
    );

    animation.finished.then(() => {
      details.open = false;
      this.#animating = false;
    }).catch(() => {
      details.open = false;
      this.#animating = false;
    });
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
